import axios from 'axios';
import { InputValidator } from '../components/Validation/InputValidation';
import { withCache, apiCache } from '../utils/apiCache';
import { REQUEST_TIMEOUT_MS, isTimeoutError, withRetry } from '../utils/requestResilience';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create a separate axios instance for password reset flows without redirect interceptor
const passwordResetAxios = axios.create({
  baseURL: API_BASE_URL
});

// Add only activity tracking to the password reset instance (no redirect)
passwordResetAxios.interceptors.request.use(
  (config) => {
    if (activityResetCallback) {
      activityResetCallback();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

passwordResetAxios.interceptors.response.use(
  (response) => {
    if (activityResetCallback) {
      activityResetCallback();
    }
    return response;
  },
  (error) => {
    // NO automatic redirect for password reset flows
    return Promise.reject(error);
  }
);

// Activity tracker for API calls
let activityResetCallback = null;

export const setActivityResetCallback = (callback) => {
  activityResetCallback = callback;
};

// Helper function to reset activity timer on API calls
const trackActivity = () => {
  if (activityResetCallback) {
    activityResetCallback();
  }
};

// Axios interceptor to track activity on requests
axios.interceptors.request.use(
  (config) => {
    trackActivity();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Axios interceptor to handle responses and potential token expiry
axios.interceptors.response.use(
  (response) => {
    trackActivity();
    return response;
  },
  (error) => {
    // Handle 401 errors (unauthorized) by clearing auth data
    if (error.response?.status === 401) {
      // Always redirect for authenticated routes
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    // 402 = the server refused because the subscription has lapsed. The session
    // is still valid, so keep the token and send them to the lock screen rather
    // than logging them out. Stamp the cached user first so the screen renders
    // the right variant immediately.
    if (error.response?.status === 402) {
      try {
        const cached = JSON.parse(localStorage.getItem('user') || '{}');
        if (cached.subscriptionStatus === 'active') cached.subscriptionStatus = 'expired';
        localStorage.setItem('user', JSON.stringify(cached));
      } catch { /* fall through to the redirect regardless */ }
      if (window.location.pathname !== '/account-locked') {
        window.location.href = '/account-locked';
      }
    }
    return Promise.reject(error);
  }
);

// Legacy sanitization functions for backward compatibility
const sanitizeInput = {
  text: (input) => InputValidator.sanitizeText(input),
  email: (input) => InputValidator.sanitizeEmail(input),
  phone: (input) => InputValidator.sanitizePhone(input),
  url: (input) => InputValidator.sanitizeUrl(input),
  number: (input) => InputValidator.sanitizeNumber(input),
  password: (input) => InputValidator.sanitizePassword(input),
  otp: (input) => InputValidator.sanitizeOtp(input),
};

export const login = async (email, password) => {
  try {
    const sanitizedPassword = sanitizeInput.password(password);
    const trimmed = (email || '').trim();

    // Detect if input is an email (has '@' AND a domain with '.')
    // This allows usernames like "JoelDoyle@Witherslack" to be treated as usernames
    const isEmail = trimmed.toLowerCase().includes('@') && trimmed.toLowerCase().split('@')[1]?.includes('.');

    let normalizedInput;
    if (isEmail) {
      // Treat as email - lowercase and sanitize
      normalizedInput = sanitizeInput.email(trimmed.toLowerCase());
    } else {
      // Treat as username - preserve case, allow letters/digits/@/-
      normalizedInput = trimmed.replace(/[^a-zA-Z0-9@-]/g, '');
    }

    // Send the correct field name based on input type
    const requestBody = isEmail
      ? { email: normalizedInput, password: sanitizedPassword }
      : { username: normalizedInput, password: sanitizedPassword };

    const response = await axios.post(`${API_BASE_URL}/custom-login`, requestBody);
    if (response.data && response.data.result.token) {
      return response.data.result;
    } else {
      throw new Error("Invalid username and/or password");
    }
  } catch (error) {
    throw new Error(error.response?.data?.message || "Authentication failed");
  }
};

export const ssoUserLogin = async (ssoTokenId) => {
  try {
    if (!ssoTokenId) {
      throw new Error("SSO token identifier is required");
    }
    
    const response = await axios.post(`${API_BASE_URL}/user/ssologin`, { 
      id: ssoTokenId 
    });
    
    if (response.data && response.data.result && response.data.result.token) {
      return response.data.result;
    } else {
      throw new Error("Invalid SSO token or login failed");
    }
  } catch (error) {
    console.error("SSO login error:", error);
    throw new Error(error.response?.data?.message || error.message || "SSO authentication failed");
  }
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
  document.documentElement.style.setProperty('--primary-color', '#4CAF50');

  // Clear welcome popup flags so it shows again on next login
  sessionStorage.removeItem('hasSeenWelcomePopup');
  localStorage.removeItem('hasSeenWelcomePopup');
  localStorage.removeItem('welcomeTourStarted');
  localStorage.removeItem('tooltipAudioEnabled');

  // Clear API cache on logout
  apiCache.clear();

  // Clear the activity reset callback
  activityResetCallback = null;
};

export const updateUser = async (userData) => {
  const token = localStorage.getItem('token');
  try {
    
    // Deep copy to avoid modifying the original object
    const payload = JSON.parse(JSON.stringify(userData));
    
    // clubId is server-derived; strip it from the payload so it can't be spoofed
    delete payload.clubId;

    // Skip email validation entirely if not provided
    // Email updates are now handled separately or not at all
    if (payload.email && typeof payload.email === 'string') {
      delete payload.email; // Remove email from payload to prevent accidental updates
    }
    
    // Sanitize name field if provided
    if (payload.name && typeof payload.name === 'string') {
      const nameValidation = InputValidator.validateAndSanitize(payload.name, 'text', 'name');
      if (nameValidation.isValid) {
        payload.name = nameValidation.sanitizedValue;
      } else {
        throw new Error(`Invalid name: ${nameValidation.errorMessage}`);
      }
    }

    // Handle profileFields array with complex nested structures
    if (Array.isArray(payload.profileFields)) {
      payload.profileFields = payload.profileFields.map((field, index) => {
        
        // Skip validation for certain field types that should remain untouched
        const skipValidationFields = ['image', 'list', 'array'];
        const skipTitles = ['Profile Picture', 'Profile Image'];
        
        if (skipValidationFields.includes(field.field) || 
            skipTitles.includes(field.title) ||
            field.type === 'array') {
          return field; // Return field as-is for complex structures
        }
        
        // Only sanitize simple string values
        if (field.value && typeof field.value === 'string') {
          let fieldType = 'text';
          
          // Determine field type based on field properties
          if (field.title === 'Contact' || field.title === 'Contact Number') {
            if (field.value.includes('@')) {
              fieldType = 'email';
            } else {
              fieldType = 'phone';
            }
          } else if (field.title === 'Bio' || field.title === 'Address') {
            fieldType = 'textarea';
          } else if (field.type === 'number') {
            fieldType = 'number';
          }
          
          // ADDED: Length validation for Address field (reasonable limit)
          if (field.title === 'Address') {
            if (field.value.length > 500) {
              throw new Error(`Address cannot exceed 500 characters. Current length: ${field.value.length}`);
            }
          }
          
          // ADDED: Length validation for Contact field
          if (field.title === 'Contact') {
            if (field.value.length > 100) {
              throw new Error(`Contact cannot exceed 100 characters. Current length: ${field.value.length}`);
            }
          }
          
          try {
            const validation = InputValidator.validateAndSanitize(field.value, fieldType, field.title);
            if (validation.isValid) {
              return {
                ...field,
                value: validation.sanitizedValue
              };
            } else {
              console.warn(`Validation failed for ${field.title}:`, validation.errorMessage);
              throw new Error(`Invalid ${field.title}: ${validation.errorMessage}`);
            }
          } catch (error) {
            console.warn(`Error validating field ${field.title}:`, error);
            throw error; // Re-throw validation errors instead of returning original field
          }
        }
        
        return field; // Return field as-is if no processing needed
      });
    }
    
    // Remove any fields that shouldn't be sent to the API
    const fieldsToRemove = ['_rid', '_self', '_etag', '_attachments', '_ts', 'email'];
    fieldsToRemove.forEach(field => {
      if (payload[field]) delete payload[field];
    });
    
    const response = await axios.put(`${API_BASE_URL}/user`, payload, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json'
      },
      // A stalled request must not leave a section stuck on "Saving…".
      timeout: REQUEST_TIMEOUT_MS,
    });
    if (response.data) {
      // Invalidate user data cache after successful update
      apiCache.invalidate('user-data');
      // Completion percentages are derived from the profile, so they are stale too
      apiCache.invalidate('box-to-box');

      return response.data;
    } else {
      throw new Error("Failed to update user data");
    }
  } catch (error) {
    console.error('updateUser error:', error);
    console.error('Error response:', error.response?.data); // Debug log
    if (isTimeoutError(error)) {
      throw new Error('The server did not respond in time. Your changes may not have saved — please check and try again.');
    }
    throw new Error(error.response?.data?.message || error.message || "Error updating user data");
  }
};

// Save helper that re-fetches the LATEST user record, applies only the caller's
// change to it, then persists. Because the profile renders many sections at once
// and each previously saved its own mount-time copy of the whole user object, one
// section's save could overwrite another section's just-saved data. Merging onto a
// fresh read (getUser's cache is invalidated by every updateUser) prevents that.
// `mutate` receives the fresh user object and must mutate ONLY its own field(s).
export const updateUserMerge = async (mutate) => {
  const response = await getUser();
  const latest = response?.result?.user;
  if (!latest) {
    throw new Error('Could not load latest user data');
  }
  const updated = { ...latest };
  await mutate(updated);
  return updateUser(updated);
};

// Internal function without caching
const _getUser = async () => {
  const token = localStorage.getItem('token');
  const startedAt = Date.now();
  try {
    const response = await withRetry(
      ({ timeout }) => axios.get(`${API_BASE_URL}/user`, {
        headers: {
          'Authorization': `${token}`,
        },
        timeout,
      }),
      { label: 'getUser' }
    )();
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to fetch user data");
    }
  } catch (error) {
    console.error(`[getUser] failed after ${Date.now() - startedAt}ms:`, error.message);
    if (isTimeoutError(error)) {
      throw new Error('The server did not respond in time. Please check your connection and try again.');
    }
    throw new Error(error.response?.data?.message || "Error fetching user data");
  }
};

// Cached version with 5-minute TTL
export const getUser = withCache(_getUser, {
  ttl: 5 * 60 * 1000, // 5 minutes
  key: 'user-data'
});

// Password Reset Functions

// Step 1: Request password reset (sends email with code)
export const requestPasswordReset = async (email) => {
  try {
    // Validate and sanitize email input
    const sanitizedEmail = sanitizeInput.email(email);
    
    // Convert email to lowercase for consistency
    const normalizedEmail = sanitizedEmail.toLowerCase().trim();
    
    const response = await passwordResetAxios.post('/user/password-reset', { 
      email: normalizedEmail 
    });
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to send reset email");
    }
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error sending reset email");
  }
};

// Step 2: Validate OTP code from email
export const validateOtpCode = async (email, otp) => {
  try {
    
    // Validate inputs before sanitization
    if (!email || !otp) {
      throw new Error("Email and OTP code are required");
    }
    
    // Process inputs - email as string, OTP as NUMBER (this is the key fix!)
    const processedEmail = String(email).toLowerCase().trim();
    const processedOtp = parseInt(String(otp).trim(), 10); // Convert to number!
    
    
    const response = await passwordResetAxios.post('/user/otp-validation', {
      email: processedEmail,
      otp: processedOtp  // Send as number, not string
    });
    
    
    // Match iOS app - expect response.data.result with userId and token
    if (response.data && response.data.result) {
      return response.data;
    } else {
      throw new Error("Invalid OTP code");
    }
  } catch (error) {
    console.error('validateOtpCode error:', error);
    console.error('Error response:', error.response?.data);
    
    // Provide more specific error messages
    if (error.response?.status === 401) {
      throw new Error("Invalid or expired verification code");
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || "Invalid request");
    } else if (error.response?.status === 404) {
      throw new Error("User not found or reset request expired");
    } else {
      throw new Error(error.response?.data?.message || "Failed to validate verification code");
    }
  }
};

// Step 3: Reset password with userId and token (from email link or OTP validation response)
export const resetPassword = async (userId, token, newPassword, confirmPassword) => {
  try {
    
    // Validate inputs
    if (!userId || !token) {
      throw new Error("Missing required reset parameters");
    }
    
    // Validate password inputs
    const sanitizedNewPassword = sanitizeInput.password(newPassword);
    const sanitizedConfirmPassword = sanitizeInput.password(confirmPassword);
    
    // Validate that passwords match
    if (sanitizedNewPassword !== sanitizedConfirmPassword) {
      throw new Error("Passwords do not match");
    }
    
    const response = await passwordResetAxios.post(`/user/password-reset/${userId}/${token}`, {
      password: sanitizedNewPassword,
      confirmPassword: sanitizedConfirmPassword
    });
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to reset password");
    }
  } catch (error) {
    console.error('resetPassword error:', error);
    throw new Error(error.response?.data?.message || "Failed to reset password");
  }
};